import { useState } from "react";
import { Link } from "react-router-dom";

const AI_ANSWERS = [
  {
    q: "What does a tree service company do?",
    a: "A professional tree service company provides expert care for trees on residential and commercial properties. Services include tree removal (safely felling and hauling away trees), tree trimming and pruning (shaping canopy for health and safety), stump grinding (removing stumps below ground), and emergency storm response. Licensed arborists assess tree health and use specialized equipment to protect your property.",
    icon: "ri-tree-fill",
  },
  {
    q: "When should a tree be removed vs. trimmed?",
    a: "Remove a tree when it's dead, severely diseased beyond recovery, structurally compromised (hollow trunk, major lean toward structures), or posing an immediate safety hazard to your home. Trim trees when they're overgrown, have dead branches, block light, or encroach on rooflines — but the tree is otherwise healthy. Our ISA Certified Arborists can evaluate your tree and recommend the safest, most cost-effective solution.",
    icon: "ri-question-answer-fill",
  },
  {
    q: "What affects the cost of tree services?",
    a: "Tree removal cost is primarily determined by tree height and trunk diameter. Other factors include proximity to structures (houses, power lines, fences), accessibility for equipment, number of trees, and whether stump grinding is included. Emergency or same-day service may incur a premium. We provide free, transparent written estimates with no hidden fees so you know the exact price before work begins.",
    icon: "ri-money-dollar-circle-fill",
  },
  {
    q: "Why is professional tree removal important?",
    a: "Improper tree removal is one of the leading causes of property damage and personal injury among DIY homeowners. Large trees falling in the wrong direction can crush homes, vehicles, or injure bystanders. Professional arborists use precision rigging, trained climbing techniques, and OSHA-compliant safety protocols to control every cut. Our crew is fully insured, protecting you from liability if anything goes wrong.",
    icon: "ri-shield-check-fill",
  },
];

const VOICE_FAQS = [
  {
    q: "Who provides tree removal near me in Northern Virginia?",
    a: "Diversified Tree Services LLC provides professional tree removal across Northern Virginia including Fredericksburg, Stafford County, Spotsylvania, King George, Culpeper, Warrenton, Manassas, and Woodbridge. We offer free on-site estimates, same-day response, and 24/7 emergency service. Call (540) 776-4488 to schedule.",
  },
  {
    q: "How much does tree removal cost in Fredericksburg, VA?",
    a: "Tree removal in Northern Virginia typically costs between $350 and $2,500 depending on tree size, location, and complexity. Small trees under 30 feet start around $350, while large trees over 80 feet average $1,200–$2,500. Stump grinding starts at $150 extra. We provide free written estimates with no hidden fees.",
  },
  {
    q: "How long does tree removal take to complete?",
    a: "Most residential tree removals are completed in 2 to 6 hours. A small or mid-sized tree typically takes 2–3 hours including cleanup. Large, complex removals near structures may take a full day. Our crew works efficiently and performs complete debris cleanup before leaving your property.",
  },
  {
    q: "When should I trim or remove a tree?",
    a: "Remove a tree when it's dead, severely diseased, structurally unsafe, or posing direct danger to your home. Trim trees annually in late winter or early spring to maintain health and shape. If branches hang over your roof or utility lines, call for an assessment right away. Our certified arborists provide free evaluations.",
  },
  {
    q: "Do you offer emergency tree removal after a storm?",
    a: "Yes, Diversified Tree Services LLC provides 24/7 emergency tree removal for storm-damaged, fallen, or hazardous trees across Northern Virginia. Call (540) 776-4488 immediately for emergency response. We prioritize dangerous situations and dispatch as quickly as possible to protect your home.",
  },
  {
    q: "Are you licensed and insured for tree removal in Virginia?",
    a: "Yes. Diversified Tree Services LLC is fully licensed and carries comprehensive general liability insurance and workers' compensation coverage. We're happy to provide proof of insurance before any work begins. Our team includes ISA Certified Arborists following industry safety standards on every job.",
  },
];

export default function FAQSection() {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <section className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-[#e8632a] font-bold text-sm uppercase tracking-[3px] block mb-4">Knowledge Center</span>
          <h2 className="text-4xl lg:text-5xl font-black text-[#1e1e1e] mb-4">
            Expert Answers &amp; FAQs
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            Straight answers to the questions homeowners ask most — optimized for Google, AI search, and voice assistants.
          </p>
        </div>

        {/* AI Answer Blocks — 2x2 grid */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-8 h-8 flex items-center justify-center bg-[#1a3a2e] rounded-lg">
              <i className="ri-robot-fill text-[#e8632a] text-sm"></i>
            </div>
            <h3 className="font-black text-[#1e1e1e] text-lg uppercase tracking-wide">AI Search Answer Blocks</h3>
            <div className="h-px flex-1 bg-gray-100"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {AI_ANSWERS.map((item) => (
              <div key={item.q} className="bg-[#f8f6f3] rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#e8632a] rounded-xl flex-shrink-0">
                    <i className={`${item.icon} text-white text-base`}></i>
                  </div>
                  <div>
                    <h4 className="font-black text-[#1e1e1e] text-sm mb-2 leading-snug">{item.q}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Voice FAQ Accordion */}
        <div>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-8 h-8 flex items-center justify-center bg-[#1a3a2e] rounded-lg">
              <i className="ri-mic-fill text-[#e8632a] text-sm"></i>
            </div>
            <h3 className="font-black text-[#1e1e1e] text-lg uppercase tracking-wide">Voice Search FAQs</h3>
            <div className="h-px flex-1 bg-gray-100"></div>
          </div>

          <div className="space-y-2">
            {VOICE_FAQS.map((faq, i) => (
              <div
                key={i}
                className={`rounded-2xl overflow-hidden border transition-all ${open === i ? "border-[#e8632a]/30 bg-[#fff5f0]" : "border-gray-100 bg-[#f8f6f3]"}`}
              >
                <button
                  className="w-full flex items-center gap-4 px-6 py-5 text-left cursor-pointer hover:bg-[#fff5f0] transition-colors"
                  onClick={() => setOpen(open === i ? null : i)}
                >
                  <i className="ri-mic-2-line text-[#e8632a] flex-shrink-0"></i>
                  <span className="font-bold text-[#1e1e1e] text-base flex-1 pr-4">{faq.q}</span>
                  <div
                    className={`w-8 h-8 flex items-center justify-center rounded-full border-2 flex-shrink-0 transition-all ${
                      open === i ? "bg-[#e8632a] border-[#e8632a] text-white" : "border-gray-300 text-gray-400"
                    }`}
                  >
                    <i className={`text-sm ${open === i ? "ri-subtract-line" : "ri-add-line"}`}></i>
                  </div>
                </button>
                {open === i && (
                  <div className="px-6 pb-5 pl-16">
                    <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 bg-[#1a3a2e] rounded-3xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-white font-black text-xl mb-2">Still Have Questions?</h3>
            <p className="text-white/60 text-sm">Our certified arborists are happy to talk through your specific situation — no obligation.</p>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            <a href="tel:+15407764488" className="flex items-center gap-2 px-6 py-3 bg-[#e8632a] text-white font-black rounded-xl uppercase tracking-wide hover:bg-[#d0541e] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill"></i>(540) 776-4488
            </a>
            <Link to="/faq" className="px-6 py-3 border border-white/30 text-white font-bold rounded-xl hover:border-white transition-all cursor-pointer whitespace-nowrap text-sm">
              Full FAQ Page
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
